import { Sphere360Camera } from 'Framework/Components/Controls/XView/Cameras';
import { IConfiguration, ICoordinates } from 'Framework/Components/Controls/XView/Core';
import { Viewer360 } from 'Framework/Components/Controls/XView/XView.Viewer360';
import { JsonProperty, PropSyncWithInternal, editorBrowsable } from 'Framework/Components/Panel.Commons';
import { Component, PropSync, UIElement, Watch } from 'Framework/Components/UIElement';
import { MathHelper } from 'Framework/Helpers/MathHelper';

@Component({
    name: 'x-view'
})
export default class XView extends UIElement {
    public mounted() {
        this.$nextTick(function () {
            const ro = new ResizeObserver(entries => {
                clearTimeout(this._resizeTimer);
                this._resizeTimer = setTimeout(() => {
                    let parentRectangle = this.canvasElement.parentElement.getBoundingClientRect();
                    let rectangle = this.canvasElement.getBoundingClientRect();

                    let finalRectangle: DOMRect = new DOMRect(
                        rectangle.x,
                        rectangle.y,
                        rectangle.width > parentRectangle.width ? parentRectangle.width : rectangle.width,
                        rectangle.height > parentRectangle.height ? parentRectangle.height : rectangle.height
                    );

                    if (this._viewer) {
                        this._viewer.setSizeView(finalRectangle.width, finalRectangle.height);
                    }
                }, 200);
            });
            ro.observe(this.canvasElement);

            this._isMounted = true;
            if (this._mountedCallback) {
                this._mountedCallback();
                this._mountedCallback = null;
            } else if (this._configuration) {
                this.loadConfiguration(this._configuration);
            }
        });
    }

    public beforeDestroy(): void {
        UIElement.Bus.$off('refresh-hotspots');

        //ro.ro.disconnect ?

        /**
         * Called manually because viewer is not mounted by vue so $destroy does not work
         */
        this._viewer?.beforeDestroy();
    }

    public onCanvasClick(e: MouseEvent): void {
        UIElement.Bus.$emit('xview-click', { sender: this._viewer, args: e });
    }

    public onDragOverItem(event: DragEvent): void {
        if (this._isDropAllowed) {
            event.preventDefault();
        }
    }

    public onDropItem(event: DragEvent): void {
        if (this._isDropAllowed) {
            event.preventDefault();

            UIElement.Bus.$emit('x-view-on-drop-item',
                this,
                MathHelper.toSphericalCoordinates(this._viewer.pick(event.offsetX, event.offsetY)),
                event
            );
        }
    }

    @Watch('configuration')
    public onConfigurationPropertyChanged(value: IConfiguration, oldValue: IConfiguration): void {
        if (this._isMounted) {
            this.loadConfiguration(value);
        } else {
            this._mountedCallback = () => { this.loadConfiguration(value); };
        }
    }

    private async loadConfiguration(configuration: IConfiguration): Promise<void> {
        let viewer: Viewer360 = this._viewer;

        if (viewer) {
            let screenshotData = await viewer.takeScreenshotAsync();

            this._showPreview(screenshotData);
        }
        else {
            let updateCurrentOrientation = this.$throttle(this._updateCurrentOrientation, 50);

            viewer = new Viewer360(
                this.canvasElement,
                configuration.iconProvider,
                cam => {
                    updateCurrentOrientation(cam);
                }
            );
        }

        try {
            this._viewer = viewer;
            UIElement.Bus.$once('preview-loaded', this._hidePreview);
            await this.$nextTick();
            await viewer.loadConfigurationAsync(configuration);
        }
        catch (error) {
            console.error(error);
            //Todo display error to user.
            this._viewer = null;
        }
        finally {
            this.$emit('viewer-changed', this._viewer);
        }
    }

    private _updateCurrentOrientation(cam: Sphere360Camera) {
        let target = cam.getTarget();
        //ASTM E57 != BABYLON Vector3 Coordinates system
        //Convert Babylon to ASTM E57
        this._orientation = {
            x: target.z,
            y: -target.x,
            z: target.y
        };
        this._focale = cam.fov;
    }

    private _showPreview(sourcePreview: string): void {
        let transitionElement = this.transitionElement;

        if (transitionElement && sourcePreview) {
            transitionElement.src = sourcePreview;
            transitionElement.className = 'preview-image-visible';
        }
    }

    private _hidePreview(): void {
        let transitionElement = this.transitionElement;

        if (transitionElement && transitionElement.className === 'preview-image-visible') {
            transitionElement.className = 'preview-image-not-visible';
        }
    }

    public get viewer(): Viewer360 {
        return this._viewer;
    }

    private get transitionElement(): HTMLImageElement {
        return this.$refs['transitionElement'] as HTMLImageElement;
    }

    private get canvasElement(): HTMLCanvasElement {
        return this.$refs['canvasElement'] as HTMLCanvasElement;
    }

    @JsonProperty({ className: 'x-view', name: 'configuration', type: Object })
    @editorBrowsable({ groupName: 'Commons' })
    @PropSyncWithInternal('configuration', { type: Object, required: false, default: null })
    public _configuration: IConfiguration;

    @JsonProperty({ className: 'x-view', name: 'currentOrientation', type: Object })
    @editorBrowsable({ groupName: 'Commons' })
    @PropSyncWithInternal('currentOrientation', { type: Object, required: false, default: null })
    public _orientation: ICoordinates;

    @JsonProperty({ className: 'x-view', name: 'currentFocale', type: Number })
    @editorBrowsable({ groupName: 'Commons', template: 'Number' })
    @PropSyncWithInternal('currentFocale', { type: Number, required: false, default: null })
    public _focale: number;

    @JsonProperty({ className: 'x-view', name: 'isDropAllowed', type: Boolean })
    @editorBrowsable({ groupName: 'Commons', template: 'Boolean' })
    @PropSync('isDropAllowed', { type: Boolean, required: false, default: false })
    public _isDropAllowed: boolean;

    private _viewer: Viewer360 = null;
    private _isMounted: boolean = false;
    private _mountedCallback: Function = null;
    private _resizeTimer: number;
}
